import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart gift message (snippets/cart-summary.liquid). A discount-style row that
 * opens a small modal; saving writes the order note (`cart.note`), removing
 * clears it. Updates the row in place — no section re-render needed, the note
 * doesn't affect totals.
 *
 * @extends {Component}
 */
class CartGiftMessage extends Component {
  get #dialog() {
    return /** @type {HTMLDialogElement|null} */ (this.querySelector('[data-gift-message-dialog]'));
  }

  get #text() {
    return /** @type {HTMLTextAreaElement|null} */ (this.querySelector('[data-gift-message-text]'));
  }

  connectedCallback() {
    super.connectedCallback();
    // Backdrop click closes (the dialog element itself is the backdrop target).
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });
  }

  open() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    this.#showError(false);
    this.count();
    dialog.showModal();
    const text = this.#text;
    text?.focus();
    text?.setSelectionRange(text.value.length, text.value.length);
  }

  close() {
    this.#dialog?.close();
  }

  /** Character counter under the textarea ("12/250"). */
  count() {
    const counter = /** @type {HTMLElement|null} */ (this.querySelector('[data-gift-message-count]'));
    if (!counter) return;
    const length = this.#text?.value.length ?? 0;
    counter.textContent = (counter.dataset.template || '').replace('[count]', String(length));
  }

  async save() {
    await this.#update((this.#text?.value || '').trim());
  }

  async remove() {
    await this.#update('');
  }

  /** @param {string} note */
  async #update(note) {
    const buttons = this.querySelectorAll('dialog button');
    buttons.forEach((button) => button.setAttribute('disabled', ''));
    this.#showError(false);
    try {
      const res = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ note }) }));
      if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
      this.#render(note);
      this.close();
    } catch (error) {
      console.error('[cart-gift-message]', error);
      this.#showError(true);
    } finally {
      buttons.forEach((button) => button.removeAttribute('disabled'));
    }
  }

  /** @param {string} note */
  #render(note) {
    const preview = this.querySelector('[data-gift-message-preview]');
    const trigger = this.querySelector('[data-gift-message-trigger]');
    const removeButton = /** @type {HTMLElement|null} */ (this.querySelector('[data-gift-message-remove]'));
    if (preview) {
      preview.textContent = note || this.dataset.placeholder || '';
      preview.classList.toggle('cart-gift-message__preview--empty', !note);
    }
    if (trigger) trigger.textContent = (note ? this.dataset.labelEdit : this.dataset.labelAdd) || '';
    if (removeButton) removeButton.hidden = !note;
    if (this.#text) this.#text.value = note;
    this.count();
  }

  /** @param {boolean} show */
  #showError(show) {
    const error = /** @type {HTMLElement|null} */ (this.querySelector('[data-gift-message-error]'));
    if (error) error.hidden = !show;
  }
}

if (!customElements.get('cart-gift-message')) {
  customElements.define('cart-gift-message', CartGiftMessage);
}
